// Typed calls for the price-list import (Sprint 4; packages/contracts/src/openapi/inventory.ts §5.21; UC-IMPORTS-001..006): the
// upload, an import's terms and its discard, the column mapping and the saved profiles, the check, the working list and its ticks,
// Apply and Resume, and the two signed downloads. Every route checks `imports.job.read` or `imports.job.write` and, on a price-list
// import, `pricing.price_list.write` (BR-IMPORTS-001); no route asks for a Control PIN. The upload, Apply and a new profile carry
// an `Idempotency-Key`; the rest are naturally idempotent. Response schemas are the contract's own (TEN-73).
import {
  ImportJobPageSchema, ImportJobViewSchema, ImportJobWriteResultSchema, ImportProfilePageSchema, ImportProfileViewSchema, ImportRowPageSchema,
  type ImportCommitMode, type ImportJobPatch, type ImportJobStatus, type ImportMappingInput, type ImportProfileCreate, type ImportProfilePatch, type ImportRowVerdict, type ImportScope,
} from "@visionx/contracts";
import { API_BASE, request } from "./client";
import type { Cursor } from "./endpoints";

/** UC-IMPORTS-001 step 3: the kind, the supplier, the scope, the list's date and the file inline; terms left at their defaults are not sent. */
export interface UploadBody {
  kind: "PriceList";
  supplierId: string;
  scope: ImportScope;
  listDate: string;
  listName?: string | undefined;
  lensMarkupBp?: number | undefined;
  discountBp?: number | null | undefined;
  file: { fileName: string; data: string };
}

export interface ImportListFilters { supplierId?: string | undefined; status?: readonly ImportJobStatus[] | undefined }

/** UC-IMPORTS-003: the working list's filters — a status (or the lines to review), a design, a range, ticked or not. */
export interface RowFilters {
  verdict?: readonly ImportRowVerdict[] | undefined;
  review?: boolean | undefined;
  design?: string | undefined;
  range?: string | undefined;
  ticked?: boolean | undefined;
}

/** BR-IMPORTS-009: named lines, or every line a filter selects on every page. */
export type SelectionBody =
  | { ticked: boolean; rows: string[] }
  | { ticked: boolean; filter: { verdict?: ImportRowVerdict | undefined; design?: string | undefined; range?: string | undefined } };

const listOf = <T extends string>(v: readonly T[] | undefined): string | undefined => (v && v.length > 0 ? v.join(",") : undefined);
const flag = (v: boolean | undefined): "true" | "false" | undefined => (v === undefined ? undefined : v ? "true" : "false");

export const importJobs = {
  /** UC-IMPORTS-005 steps 1–2, A1: newest first, a page at a time; `count` adds the total. */
  list: (f: ImportListFilters, c: Cursor & { count?: boolean | undefined } = {}) => request("/import-jobs", {
    query: { kind: "PriceList", supplierId: f.supplierId, status: listOf(f.status), limit: c.limit ?? 25, cursor: c.cursor, count: c.count ? "true" : undefined },
    schema: ImportJobPageSchema,
  }),
  /** UC-IMPORTS-005 step 3: its terms, its mapping, its counts, its progress or failure and the moves it allows. */
  get: (id: string) => request(`/import-jobs/${id}`, { schema: ImportJobViewSchema }),
  /** UC-IMPORTS-001: `201` Uploaded, with `same_file` when the supplier's file was imported before (A5). */
  upload: (body: UploadBody, idempotencyKey: string) => request("/import-jobs", { method: "POST", body, idempotencyKey, schema: ImportJobWriteResultSchema }),
  /** UC-IMPORTS-001 A7: the list's name, date, lens markup, discount or scope; a new scope or term sends a checked import back. */
  patch: (id: string, body: ImportJobPatch) => request(`/import-jobs/${id}`, { method: "PATCH", body, schema: ImportJobViewSchema }),
  /** UC-IMPORTS-006: `204` — removed, or kept as Discarded once its Apply had started (BR-IMPORTS-020). */
  discard: (id: string) => request(`/import-jobs/${id}`, { method: "DELETE" }),
  /** UC-IMPORTS-002 steps 3–5: the mapping the file is checked with; the same mapping sent back keeps the check (A5). */
  putMapping: (id: string, body: ImportMappingInput) => request(`/import-jobs/${id}/mapping`, { method: "PUT", body, schema: ImportJobViewSchema }),
  /** UC-IMPORTS-003 steps 1–2: `202` Checking; the check runs in the background. */
  check: (id: string) => request(`/import-jobs/${id}/check`, { method: "POST", body: {}, schema: ImportJobViewSchema }),
  /** UC-IMPORTS-003 step 5: the working list in file order, then the discontinued lines. */
  rows: (id: string, f: RowFilters, c: Cursor & { count?: boolean | undefined } = {}) => request(`/import-jobs/${id}/rows`, {
    query: {
      verdict: listOf(f.verdict), view: f.review && !f.verdict ? "review" : undefined, design: f.design, range: f.range, ticked: flag(f.ticked),
      limit: c.limit ?? 11, cursor: c.cursor, count: c.count ? "true" : undefined,
    },
    schema: ImportRowPageSchema,
  }),
  /** BR-IMPORTS-009: answers the import with its new counts. */
  select: (id: string, body: SelectionBody) => request(`/import-jobs/${id}/selection`, { method: "PUT", body, schema: ImportJobViewSchema }),
  /** UC-IMPORTS-004 steps 3–5: `202` Importing. */
  commit: (id: string, body: { mode: ImportCommitMode; recalculateSellingPrices: boolean }, idempotencyKey: string) =>
    request(`/import-jobs/${id}/commit`, { method: "POST", body, idempotencyKey, schema: ImportJobViewSchema }),
  /** UC-IMPORTS-004 A10: `202` Importing again, from the last finished chunk. */
  resume: (id: string) => request(`/import-jobs/${id}/resume`, { method: "POST", body: {}, schema: ImportJobViewSchema }),
  /** UC-IMPORTS-005 steps 4–5: the route answers `302` to a 15-minute signed link, served as an attachment; an anchor follows it. */
  filePath: (id: string): string => `${API_BASE}/import-jobs/${id}/file`,
  /** UC-IMPORTS-005 A2: the rejected lines as CSV, through the same kind of link. */
  errorReportPath: (id: string): string => `${API_BASE}/import-jobs/${id}/error-report`,
};

export const importProfiles = {
  /** BR-IMPORTS-006: the shipped Generic CSV and Seiko CSV layouts and the supplier's live profiles. */
  list: (supplierId: string) => request("/import-profiles", { query: { supplierId, kind: "PriceList", limit: 200 }, schema: ImportProfilePageSchema }),
  /** UC-IMPORTS-002 A2: saved for the supplier and the price-list kind. */
  create: (body: ImportProfileCreate, idempotencyKey: string) => request("/import-profiles", { method: "POST", body, idempotencyKey, schema: ImportProfileViewSchema }),
  /** UC-IMPORTS-002 A4: renamed; a shipped layout is `409 IMMUTABLE_RECORD` (`shipped`). */
  patch: (id: string, body: ImportProfilePatch) => request(`/import-profiles/${id}`, { method: "PATCH", body, schema: ImportProfileViewSchema }),
  /** UC-IMPORTS-002 A4: voided — no longer offered or recognised. */
  remove: (id: string) => request(`/import-profiles/${id}`, { method: "DELETE" }),
};
